import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import { ArrowLeft, Gamepad2, Trophy, Calendar, Layers, Loader2, ChevronRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import PlatformBadge, { SystemBadge } from "@/components/PlatformBadge";
import SlopBadge from "@/components/SlopBadge";
import UnplayableBadge from "@/components/UnplayableBadge";
import RelatedGames from "@/components/RelatedGames";

const API = `${getApiUrl()}/api`;

export default function VersionHub() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    let active = true;
    setLoading(true);
    axios.get(`${API}/master/browse/version-group/${groupId}`)
      .then(r => { if (active) { setData(r.data); setError(null); } })
      .catch(err => { if (active) setError(err.response?.data?.detail || "Version group not found"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [groupId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white/30" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#050505] p-6">
        <Button variant="ghost" onClick={() => navigate("/browse")} className="text-white/60 hover:text-white mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Browse
        </Button>
        <div className="text-center py-20 text-white/50" data-testid="version-hub-error">{error || "Not found"}</div>
      </div>
    );
  }

  const versions = data.versions || [];

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-6" data-testid="version-hub">
      <Button variant="ghost" onClick={() => navigate("/browse")} className="text-white/60 hover:text-white mb-6" data-testid="version-hub-back">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Browse
      </Button>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-cyan-400 text-xs font-medium uppercase tracking-wider mb-1">
          <Layers className="w-4 h-4" />
          {versions.length} {versions.length === 1 ? "version" : "versions"}
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">{data.name}</h1>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          {data.totalLibraryCount > 0 && (
            <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/30 font-medium" data-testid="version-hub-library-total">
              In Library{data.totalLibraryCount > 1 ? ` ×${data.totalLibraryCount}` : ""}
            </span>
          )}
          {data.totalOwnedCount > 0 && (
            <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-300 border border-green-500/30 font-medium" data-testid="version-hub-owned-total">
              Owned{data.totalOwnedCount > 1 ? ` ×${data.totalOwnedCount}` : ""}
            </span>
          )}
        </div>
        <p className="text-white/40 text-sm mt-2">These are linked versions of the same game. Pick a version to view its details, add it to your library, or add it to your wish list.</p>
      </div>

      {/* Version list */}
      <div className="space-y-3 max-w-3xl">
        {versions.map((v) => {
          const primary = v.primarySystem || (v.systems && v.systems[0]);
          const yr = (v.releaseDate || "").match(/\d{4}/)?.[0];
          return (
            <Link
              key={v.id}
              to={`/browse/${v.id}`}
              className="flex gap-4 p-4 bg-[#121212] rounded-lg border border-white/5 hover:border-cyan-500/40 transition-colors group"
              data-testid={`version-row-${v.id}`}
            >
              <div className="w-16 h-24 flex-shrink-0 overflow-hidden rounded bg-black/50">
                {v.boxArtUrl ? (
                  <img src={v.boxArtUrl} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Gamepad2 className="w-6 h-6 text-white/20" /></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-white font-medium line-clamp-2 group-hover:text-cyan-300 transition-colors">
                    {v.isPrimary && (
                      <Star className="inline w-3.5 h-3.5 text-amber-300 fill-amber-300 mr-1.5 align-middle" title="Primary version" />
                    )}
                    {v.name}
                  </p>
                  <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-cyan-300 transition-colors shrink-0" />
                </div>

                <div className="flex flex-wrap items-center gap-1 mt-1.5">
                  {v.platforms?.map(p => <PlatformBadge key={p} platform={p} size="sm" />)}
                  {primary && <SystemBadge system={primary} size="sm" />}
                  {v.region && v.region !== "WW" && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 border border-blue-500/30">{v.region}</span>
                  )}
                  {v.isAchievementSlop && <SlopBadge compact />}
                  {v.isUnplayable && <UnplayableBadge compact />}
                  {v.inWishlist && !v.inLibrary && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 font-medium" data-testid={`version-wishlist-${v.id}`}>
                      Wishlist
                    </span>
                  )}
                  {v.inLibrary && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/30 font-medium" data-testid={`version-in-library-${v.id}`}>
                      In Library{v.libraryCount > 1 ? ` ×${v.libraryCount}` : ""}
                    </span>
                  )}
                  {v.owned && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-300 border border-green-500/30 font-medium" data-testid={`version-owned-${v.id}`}>
                      Owned{v.ownedCount > 1 ? ` ×${v.ownedCount}` : ""}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-3 mt-2 text-xs text-white/50">
                  {yr && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{yr}</span>}
                  {v.totalAchievements > 0 && (
                    <span className="flex items-center gap-1"><Trophy className="w-3 h-3" />{v.totalAchievements}</span>
                  )}
                  {v.totalScore > 0 && <span className="text-yellow-400 font-medium">{v.totalScore.toLocaleString()}G</span>}
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Related games (alternate versions, remasters, remakes, collections) */}
      <div className="max-w-3xl mt-6">
        <RelatedGames relations={data.relations} />
      </div>
    </div>
  );
}
